import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiCheck, FiX } from 'react-icons/fi';
import { useLiveChallenge } from '@/hooks/useLiveChallenge';

/**
 * AnswerFeedback - Mostra se o aluno acertou + pontos + ranking.
 * Auto-navega para QuestionPage quando nova questão é revelada,
 * ou para FinalResultPage quando a sessão termina.
 */
const AnswerFeedback = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { session, lastAnswerFeedback, myScore, currentQuestion, sessionStatus, questionRevealed, questionIndex, totalQuestions, sessionReset, clearSessionReset, subscribe } = useLiveChallenge();
  const isLastQuestion = totalQuestions > 0 && questionIndex + 1 >= totalQuestions;
  const activationId = id || session?.activation_id;

  // F5 fallback: na navegação normal o subscribe foi chamado em JoinPage. Em F5
  // direto nesta página, session é null, WS nunca conecta, sessionStatus fica
  // undefined e os useEffects de navegação abaixo nunca disparam — aluno fica
  // preso vendo um feedback vazio. Subscribe é idempotente.
  useEffect(() => {
    if (activationId && !session) {
      subscribe(activationId, 'student');
    }
  }, [activationId, session, subscribe]);

  // Vai responder quando a questão atual for REVELADA. Antes navegávamos por
  // mudança de currentQuestion.id, mas após F5 entre questões o resync seta
  // currentQuestion para a PRÓXIMA questão (o current_question do backend aponta
  // pra próxima a revelar) ainda não revelada. Com id-change: (a) o aluno pulava
  // pra cá com a questão sem revelar e (b) o broadcast question_revealed seguinte
  // mantinha o mesmo id → a navegação não disparava e o aluno ficava preso na tela
  // de resultado, sem responder as próximas questões. questionRevealed dispara no
  // momento certo (reveal), independente do id. activationId (da URL) sobrevive ao
  // F5 — session?.activation_id é null e gerava /desafio-ao-vivo/undefined/...
  useEffect(() => {
    if (questionRevealed && currentQuestion?.id) {
      navigate(`/desafio-ao-vivo/${activationId}/questao`, { replace: true });
    }
  }, [questionRevealed, currentQuestion?.id, navigate, activationId]);

  useEffect(() => {
    if (sessionStatus === 'finished') {
      navigate(`/desafio-ao-vivo/${activationId}/final`, { replace: true });
    }
  }, [sessionStatus, navigate, activationId]);

  // Admin reiniciou a sessão — participante foi destruído, volta ao /entrar.
  useEffect(() => {
    if (sessionReset) {
      clearSessionReset();
      navigate('/desafio-ao-vivo/entrar', { replace: true });
    }
  }, [sessionReset, clearSessionReset, navigate]);

  const isCorrect = lastAnswerFeedback?.is_correct ?? lastAnswerFeedback?.correct ?? false;
  const pointsEarned = lastAnswerFeedback?.points_earned ?? lastAnswerFeedback?.['points-earned'] ?? 0;
  const currentRank = myScore?.rank ?? myScore?.position ?? null;

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-bright px-4 py-12">
      <div className="flex w-full max-w-md flex-col items-center gap-6 rounded-2xl bg-white px-8 py-10 text-center shadow-dropshadow-lg">
        <div className={`flex h-24 w-24 animate-[scale-in_0.4s_ease-out] items-center justify-center rounded-full text-white shadow-dropshadow-md ${isCorrect ? 'bg-success' : 'bg-danger'}`}>
          {isCorrect ? (
            <FiCheck
              className="h-12 w-12"
              strokeWidth={3}
            />
          ) : (
            <FiX
              className="h-12 w-12"
              strokeWidth={3}
            />
          )}
        </div>

        <h1 className={`m-0 text-3xl font-extrabold ${isCorrect ? 'text-success' : 'text-danger'}`}>{isCorrect ? 'Correto!' : 'Incorreto'}</h1>

        {pointsEarned > 0 && (
          <div className={`flex flex-col items-center gap-1 rounded-xl px-8 py-4 ${isCorrect ? 'bg-success-lightest' : 'bg-bright'}`}>
            <span className={`text-3xl font-extrabold ${isCorrect ? 'text-success' : 'text-neutral-dark'}`}>+{pointsEarned}</span>
            <span className="text-sm font-semibold text-neutral">pontos</span>
          </div>
        )}

        {currentRank && (
          <div className="flex flex-col items-center gap-1 rounded-xl bg-primary-lightest px-8 py-4">
            <span className="text-3xl font-extrabold text-primary-dark">{currentRank}º</span>
            <span className="text-sm font-semibold text-neutral">lugar</span>
          </div>
        )}

        <div className="mt-2 flex flex-col items-center gap-3">
          <div className="flex gap-2">
            <span className="h-2.5 w-2.5 animate-bounce rounded-full bg-primary [animation-delay:0ms]" />
            <span className="h-2.5 w-2.5 animate-bounce rounded-full bg-primary [animation-delay:150ms]" />
            <span className="h-2.5 w-2.5 animate-bounce rounded-full bg-primary [animation-delay:300ms]" />
          </div>
          <p className="m-0 text-sm font-medium text-neutral">
            {isLastQuestion ? 'Aguardando conclusão do desafio...' : 'Aguardando próxima questão...'}
          </p>
        </div>
      </div>

      <style>{`
        @keyframes scale-in {
          0% { transform: scale(0.3); opacity: 0; }
          60% { transform: scale(1.05); }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default AnswerFeedback;
